import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { forbiddenAccess } from '@interacta-shared/data-access-error';
import { TipService } from '@interacta-shared/feature-tip';
import {
    DialogButtonComponent,
    DialogButtonsComponent,
    DialogComponent,
    DialogDescriptionComponent,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { LocalFileSelectComponent } from '@interacta-shared/ui-common';
import { filterMap, isDefined, isNumberIndex } from '@interacta-shared/util';
import { PostAttachmentService } from '@interacta/data-access-post';
import { AttachmentChipComponent } from '@interacta/ui-attachment';
import { IAttachment } from '@interacta/util-post';
import {
    AttachmentsVersionsDialogState,
    AttachmentsVersionsService,
} from '@modules/attachments/services/attachments-versions.service';
import { PostDetailSection } from '@modules/core';
import { filterEmptyValuesFromObject } from '@modules/core/helpers/generic.utils';
import { GoogleAPIService } from '@modules/core/services/google-api.service';
import { DriveFileSelectComponent } from '@modules/shared-v2/components/drive-file-select/drive-file-select.component';
import { AttachmentInputService } from '@modules/shared-v2/services/attachment-input.service';
import { TranslatePipe } from '@ngx-translate/core';
import {
    BehaviorSubject,
    Observable,
    combineLatest,
    concat,
    merge,
    of,
} from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    shareReplay,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { AttachmentVersionComponent } from '../attachment-version/attachment-version.component';

@Component({
    selector: 'interacta-attachments-versions-dialog',
    templateUrl: './attachments-versions-dialog.component.html',
    providers: [AttachmentInputService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        NgClass,
        TranslatePipe,
        DialogComponent,
        DialogDescriptionComponent,
        DialogButtonsComponent,
        DialogButtonComponent,
        SeparatorComponent,
        AttachmentChipComponent,
        LocalFileSelectComponent,
        DriveFileSelectComponent,
        AttachmentVersionComponent,
    ],
})
export class AttachmentsVersionsDialogComponent {
    public attachmentsVersionsService = inject(AttachmentsVersionsService);
    public attachmentInputService = inject(AttachmentInputService);
    public googleAPIService = inject(GoogleAPIService);
    private attachmentService = inject(PostAttachmentService);
    private tipService = inject(TipService);
    private router = inject(Router);
    private gaService = inject(GoogleAnalyticsService);

    readonly newVersionAttachments$ = new BehaviorSubject<IAttachment[]>([]);
    readonly attachments$: Observable<IAttachment[]>;
    readonly chipAttachment$: Observable<IAttachment | null>;
    readonly dialogState$: Observable<AttachmentsVersionsDialogState>;
    readonly useVersionNameForAttachment$ = new BehaviorSubject<boolean>(false);
    readonly newVersionNameControl = new FormControl<string>('');
    readonly isLoadingAttachment = signal(false);
    readonly attachmentSavedVersions = signal<IAttachment[]>([]);

    readonly labelRoot = 'DASHBOARD.ATTACHMENTS.VERSIONS.';

    constructor() {
        this.dialogState$ = this.attachmentsVersionsService
            .getState()
            .pipe(shareReplay(1), takeUntilDestroyed());

        this.dialogState$
            .pipe(
                filter((state) => !!state.isOpen),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                this.isLoadingAttachment.set(false);
                this.gaService.sendPageView('Attachment Versions');
            });

        this.dialogState$
            .pipe(
                filter((state) => state.isOpen && state.content != null),
                takeUntilDestroyed(),
            )
            .subscribe((state) => {
                // The dialog provides its own AttachmentInputService
                // instance and is rendered outside the DI tree that provides
                // INPUT_IMAGE_COMPRESSION_ENABLED, so compression would never run
                // here. Forward the flag carried in the open() params.
                this.attachmentInputService.setCompressionEnabled(
                    signal(state.content.webAttachmentResizeEnabled),
                );
                this.attachmentInputService.init({
                    acceptedType: state.content.acceptedMimeType ?? undefined,
                    limit: 1,
                });
            });

        const dialogStateAttachment$ = this.dialogState$.pipe(
            filterMap((state) =>
                state.isOpen &&
                state.content?.attachment?.id != null &&
                isNumberIndex(state.content.attachment.id)
                    ? state.content.attachment
                    : undefined,
            ),
        );

        dialogStateAttachment$
            .pipe(takeUntilDestroyed())
            .subscribe((attachment) => {
                const initAttachment = (attachment.versions ?? []).filter(
                    (v) => v.inPending,
                );
                this.attachmentInputService.setInitialAttachments(
                    initAttachment,
                );
                this.changeCheck(attachment.drive != null);
                this.newVersionAttachments$.next(initAttachment);
            });

        const attachmentVersions$ = dialogStateAttachment$.pipe(
            switchMap((attachment) =>
                concat(
                    of<IAttachment[]>([]),
                    this.attachmentService.getAttachmentVersions(
                        attachment.id as number,
                    ),
                ).pipe(tap((att) => this.attachmentSavedVersions.set(att))),
            ),
            takeUntilDestroyed(),
        );

        this.attachments$ = combineLatest([
            this.newVersionAttachments$,
            attachmentVersions$,
        ]).pipe(
            tap(([newVersion]) => {
                if (newVersion?.length) {
                    this.newVersionNameControl.setValue(
                        newVersion[0].versionName ?? newVersion[0].name,
                        { emitEvent: false },
                    );
                }
            }),
            map(([newVersion, versions]) => [...newVersion, ...versions]),
        );

        this.chipAttachment$ = combineLatest([
            this.newVersionAttachments$.pipe(
                map((attachments) =>
                    attachments.length ? attachments[0] : null,
                ),
            ),
            dialogStateAttachment$,
            this.useVersionNameForAttachment$,
        ]).pipe(
            map(([newVersion, stateAttachment, useVersionName]) => {
                const att: IAttachment = {
                    ...stateAttachment,
                    ...(newVersion ?? {}),
                    name:
                        newVersion && useVersionName
                            ? newVersion.name
                            : stateAttachment.name,
                    versionNumber: 1,
                    isMediaAudio: false,
                    isMediaVideo: false,
                    isMediaImage: false,
                    isMediaGif: false,
                };

                return !att.drive ||
                    (att.drive && this.googleAPIService.driveIntegrationEnabled)
                    ? att
                    : null;
            }),
        );

        this.newVersionNameControl.valueChanges
            .pipe(
                filter(isDefined),
                distinctUntilChanged(),
                withLatestFrom(this.newVersionAttachments$),
                takeUntilDestroyed(),
            )
            .subscribe(([name, newVersions]) => {
                this.newVersionAttachments$.next(
                    newVersions.map((i, idx) =>
                        idx === 0 ? { ...i, name } : i,
                    ),
                );
            });

        this.attachmentInputService.startLoadingAttachmentEvent$
            .pipe(
                tap(() => this.isLoadingAttachment.set(true)),
                withLatestFrom(this.newVersionAttachments$),
                takeUntilDestroyed(),
            )
            .subscribe(([newAttachments, oldList]) => {
                const attachmentToReplace =
                    newAttachments.length === 1
                        ? this.getAttachmentInError(oldList, newAttachments[0])
                        : undefined;

                this.newVersionAttachments$.next(
                    attachmentToReplace
                        ? oldList.map((i) =>
                              i === attachmentToReplace ? newAttachments[0] : i,
                          )
                        : [...oldList, ...newAttachments],
                );
            });

        merge(
            this.attachmentInputService.singleAttachmentLoadedEvent$,
            this.attachmentInputService.progressItemEvent$.pipe(
                distinctUntilKeyChanged('uploadProgress'),
            ),
        )
            .pipe(
                withLatestFrom(this.newVersionAttachments$),
                map(([newItem, actualList]) =>
                    this.updateAttachmentFromList(newItem, actualList),
                ),
                takeUntilDestroyed(),
            )
            .subscribe((list) => this.newVersionAttachments$.next(list));

        this.attachmentInputService.loadAttachmentsEvent$
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.isLoadingAttachment.set(false));
    }

    trackAttachment(
        _idx: number,
        attachment: Partial<IAttachment>,
    ): number | File {
        return attachment.fileItem?._file || _idx;
    }

    goToPost(postId: number): void {
        this.attachmentsVersionsService.goToPost(postId);
    }

    goToAttachments(attachmentId: number): void {
        this.attachmentService
            .getAttachmentDetail(attachmentId)
            .pipe(
                catchError((response) => {
                    if (response.status === 403)
                        this.router.navigate([`/${forbiddenAccess}`]);
                    return of(null);
                }),
            )
            .subscribe(
                (attachment: IAttachment | null) =>
                    attachment?.post &&
                    this.router.navigate([
                        '/post',
                        attachment.post.id,
                        PostDetailSection.ATTACHMENTS.valueOf(),
                    ]),
            );
    }

    removeVersion(version: IAttachment): void {
        this.attachmentInputService.removeAttachment(version);
        this.newVersionAttachments$.next([]);
        this.isLoadingAttachment.set(false);
    }

    cancelUpload(version: IAttachment): void {
        this.attachmentInputService.cancelAttachmentsUploading([version]);
        this.tipService.info('UPLOAD.UPLOAD_CANCELED');
        this.removeVersion(version);
    }

    save(attachment: IAttachment): void {
        const newVersions = this.newVersionAttachments$.getValue();
        if (newVersions.length > 0) {
            const loadedAttachment = newVersions[0];
            const attachmentName = this.useVersionNameForAttachment$.value
                ? (this.newVersionNameControl.value ?? '')
                : attachment.name;

            const newAttachment: IAttachment = {
                ...attachment,
                ...filterEmptyValuesFromObject(loadedAttachment),
                id: attachment.id,
                versionNumber: this.attachmentSavedVersions().length + 1,
                name: attachmentName,
                versionName:
                    this.newVersionNameControl.value ?? loadedAttachment.name,
            };
            newAttachment.versions = [
                newAttachment as IAttachment,
                ...this.attachmentSavedVersions(),
            ];
            this.attachmentsVersionsService.close(newAttachment);
        } else {
            this.attachmentsVersionsService.close();
        }
    }

    addDriveAttachments(attachments: IAttachment[]): void {
        this.newVersionAttachments$.next(attachments);
    }

    changeCheck(check: boolean): void {
        this.useVersionNameForAttachment$.next(check);
    }

    private updateAttachmentFromList(
        newItem: IAttachment,
        actualList: IAttachment[],
    ): IAttachment[] {
        return actualList.map((i) =>
            i.isPartial && i.name === newItem.name ? newItem : i,
        );
    }

    private getAttachmentInError(
        oldList: IAttachment[],
        newItem: IAttachment,
    ): IAttachment | undefined {
        return oldList.find(
            (a) =>
                a.isLoadingError === true &&
                a.name === newItem.name &&
                a.markAsHandled === false,
        );
    }
}
